import React, { useRef, useState } from 'react'
import { TextInput, TouchableOpacity, Image, StyleSheet, ScrollView } from 'react-native'
import DropDownPicker from 'react-native-dropdown-picker'
import { Formik } from 'formik'
import * as yup from 'yup'
import { useAppDispatch, useAppSelector } from '../../state/hooks'
import { setUserState } from '../../state/Slices/userSlice'
import { getFunctions, httpsCallable } from 'firebase/functions'
import Colors from '../../constants/Colors'
import { formStyle, SMALL_WIDTH_SIZE, smallTextSize } from '../../constants/GlobalStyle'
import { Text, View } from '../Themed'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { auth } from '../../firebase/config'
import { provinceList } from './validProvinces'
import { useWindowDimensions } from 'react-native'
import { Stack, Text as DSText } from '../DesignSystem'

type AddressAutocompleteResult = {
	fullAddress: string
	street: string
	city: string
	provinceState: string
	country: string
	postalCode: string
}
const capitalizeFirstLetterOfEachWord = text => {
	return text.replace(/\b\w/g, char => char.toUpperCase())
}
export default function AddressForm({
	userCurrentLocation,
	handleDeliveryEstimate,
	saveAddress,
	setSaveAddress,
	isLoading,
	setIsLoading,
	userId,
	closeCallback = null,
}) {
	const dispatch = useAppDispatch()
	const { width } = useWindowDimensions()
	const { country } = useAppSelector(state => state.settings)
	const debounceRef = useRef<NodeJS.Timeout>()
	const [autocompleteResults, setAutocompleteResults] = useState<AddressAutocompleteResult[]>([])

	const [isFocus, setIsFocus] = useState(false)
	const isSmallScreen = width < SMALL_WIDTH_SIZE

	const province = provinceList.find(
		item => item.label == userCurrentLocation?.addressObject?.province
	)

	// Define schema inside component
	const addressSchema = yup.object().shape({
		street: yup.string().required('Street address is required'),
		street2: yup.string(),
		city: yup.string().required('City is required'),
		province: yup.string().required('Province is required'),
		postalCode: yup
			.string()
			.transform(value => value.replace(/\s+/g, '').toUpperCase())
			.required('A valid postal code is required')
			.matches(
				country === 'US' || country === 'USA' ? /^\d{5}(-\d{4})?$/ : /^[A-Z]\d[A-Z]\d[A-Z]\d$/,
				country === 'US' || country === 'USA' ? 'Invalid ZIP code' : 'Invalid postal code'
			),
		label: yup.string(),
		deliveryNote: yup.string(),
	})

	const handleSubmitNewAddress = async values => {
		const streetAddress = `${values.street}${values.street2 ? ', ' + values.street2 : ''}`
		const longAddress = `${streetAddress}, ${values.city}, ${values.province}, ${values.postalCode}`
		const id = Date.now()
		const newAddress = {
			id,
			longAddress,
			formattedAddress: streetAddress,
			label: values.label,
			instructions: values.deliveryNote,
			updatedAt: new Date().getMilliseconds(),
			addressObject: {
				streetAddress: values.street,
				streetAddress2: values.street2,
				city: values.city,
				province: values.province,
				postalCode: values.postalCode,
			},
		}

		setIsLoading(true)

		dispatch(setUserState({ userLocation: newAddress }))

		if (saveAddress) {
			const functions = getFunctions()
			const setDeliveryAddressForUser = httpsCallable(functions, 'v2_setDeliveryAddressForUser')
			await setDeliveryAddressForUser({
				userId,
				address: newAddress,
				orgId: global.org,
				isEdit: false,
				isDelete: false,
			})
		}

		await handleDeliveryEstimate(longAddress)
		if (closeCallback) closeCallback()
	}

	const handleAddressChange = async address => {
		if (debounceRef.current) {
			clearTimeout(debounceRef.current)
		}

		debounceRef.current = setTimeout(async () => {
			try {
				const functions = getFunctions()
				const autocompleteAddress = httpsCallable(functions, 'v2_autocompleteAddress')

				const response = (await autocompleteAddress({
					input: address,
				})) as any

				setAutocompleteResults(response.data)
			} catch (error: any) {
				console.log(error.message)
				setAutocompleteResults([])
			}
		}, 500)
	}

	return (
		<Formik
			initialValues={{
				label: '',
				street: userCurrentLocation?.addressObject?.streetAddress || '',
				street2: '',
				city: userCurrentLocation?.addressObject?.city || '',
				province: province?.value || '',
				postalCode: userCurrentLocation?.addressObject?.postalCode || '',
				deliveryNote: '',
			}}
			enableReinitialize={true}
			validateOnChange={true}
			validateOnBlur={true}
			validateOnMount={false}
			validationSchema={addressSchema}
			onSubmit={handleSubmitNewAddress}
		>
			{({ handleChange, handleBlur, handleSubmit, setFieldValue, values, touched, errors }) => (
				<View style={styles.formContainer}>
					{/* <GooglePlacesAutocomplete
						placeholder="Search address"
						onPress={(data, details = null) => {
							// 'details' is provided when fetchDetails = true
							console.log(data, details)
						}}
						query={{
							key: GOOGLE_API_KEY,
							language: 'en',
						}}
						requestUrl={{
							url: 'https://maps.googleapis.com/maps/api/js',
							useOnPlatform: 'all',
							headers: {
								cors: 'true',
							},
						}}
					/> */}
					<TextInput
						style={[formStyle.textInput, styles.input]}
						placeholder="Street address"
						placeholderTextColor="#aaa"
						onChangeText={async text => {
							handleAddressChange(values.street)
							handleChange('street')(text)
						}}
						value={capitalizeFirstLetterOfEachWord(values.street)}
						onBlur={handleBlur('street')}
					/>
					<View style={{ zIndex: 50, position: 'relative' }}>
						{autocompleteResults.length > 0 && (
							<ScrollView style={[formStyle.textInput, { width: '100%', position: 'absolute' }]}>
								{autocompleteResults.map((data, index) => (
									<TouchableOpacity
										key={index}
										style={{ marginVertical: 10 }}
										onPress={() => {
											setFieldValue('street', data.street)
											setFieldValue('city', data.city)
											setFieldValue('province', data.provinceState)
											setFieldValue('postalCode', data.postalCode)
											setAutocompleteResults([])
										}}
									>
										<Text>{data.fullAddress}</Text>
									</TouchableOpacity>
								))}
							</ScrollView>
						)}
					</View>
					<DSText level="small" tone="critical">
						<>{touched.street && errors.street?.toString()}</>
					</DSText>
					<View
						style={{
							...formStyle.inlineContainer,
							// Added to make form responsive.
							...(isSmallScreen ? { flexDirection: 'column', gap: 0 } : {}),
						}}
					>
						<View style={formStyle.inlineItem}>
							<TextInput
								style={[formStyle.textInput, styles.input]}
								placeholder="Apt / Suite (optional)"
								placeholderTextColor="#aaa"
								onChangeText={handleChange('street2')}
								value={values.street2}
								onBlur={handleBlur('street2')}
							/>
							<DSText level="small" tone="critical">
								<></>
							</DSText>
						</View>
						<View style={formStyle.inlineItem}>
							<TextInput
								style={[formStyle.textInput, styles.input]}
								placeholder="City"
								placeholderTextColor="#aaa"
								onChangeText={handleChange('city')}
								value={capitalizeFirstLetterOfEachWord(values.city)}
								onBlur={handleBlur('city')}
							/>
							<DSText level="small" tone="critical">
								<>{touched.city && errors.city?.toString()}</>
							</DSText>
						</View>
					</View>
					<View
						style={{
							...formStyle.inlineContainer,
							// Added to make form responsive.
							...(isSmallScreen ? { flexDirection: 'column', gap: 0 } : {}),
						}}
					>
						<View style={formStyle.inlineItem}>
							<DropDownPicker
								listMode="SCROLLVIEW"
								open={isFocus}
								value={values.province}
								items={provinceList}
								setOpen={setIsFocus}
								setValue={callback => {
									const selectedValue = callback(values.province)
									setFieldValue('province', selectedValue)
								}}
								placeholder="Province"
								placeholderStyle={{ color: '#aaa' }}
								style={[formStyle.textInput, styles.input]}
								dropDownContainerStyle={{ borderColor: Colors.custom.transparentGrey }}
								searchPlaceholder="Search province..."
								searchContainerStyle={{
									zIndex: 10,
									borderColor: Colors.custom.transparentGrey,
									borderBottomColor: Colors.custom.transparentGrey,
									padding: 4,
								}}
								searchTextInputStyle={{ borderColor: Colors.custom.transparentGrey }}
								scrollViewProps={{ style: { borderColor: Colors.custom.transparentGrey } }}
								zIndex={10}
								searchable
							/>
							<DSText level="small" tone="critical">
								<>{touched.province && errors.province?.toString()}</>
							</DSText>
						</View>
						<View style={[formStyle.inlineItem, { zIndex: -1 }]}>
							<TextInput
								style={[formStyle.textInput, styles.input]}
								placeholder="Postal code"
								placeholderTextColor="#aaa"
								onChangeText={handleChange('postalCode')}
								value={values.postalCode?.toUpperCase()}
								onBlur={handleBlur('postalCode')}
							/>
							<DSText level="small" tone="critical">
								{touched.postalCode && errors.postalCode?.toString()}
							</DSText>
						</View>
					</View>
					<TextInput
						style={[formStyle.textInput, styles.input]}
						placeholder="Delivery instructions (optional)"
						placeholderTextColor="#aaa"
						onChangeText={handleChange('deliveryNote')}
						value={values.deliveryNote}
						onBlur={handleBlur('deliveryNote')}
					/>
					<DSText level="small" tone="critical">
						<></>
					</DSText>
					<TextInput
						style={[formStyle.textInput, styles.input]}
						placeholder="Address label (optional)"
						placeholderTextColor="#aaa"
						onChangeText={handleChange('label')}
						value={values.label}
						onBlur={handleBlur('label')}
					/>
					{auth.currentUser && (
						<TouchableOpacity
							onPress={() => setSaveAddress(!saveAddress)}
							style={[styles.saveAddressToggle, { zIndex: -1 }]}
						>
							<MaterialCommunityIcons
								name={saveAddress ? 'checkbox-marked-circle' : 'checkbox-blank-circle-outline'}
								size={26}
								color={saveAddress ? global.orgColor : '#818181'}
							/>
							<Text style={styles.saveAddressText}>Save address for future orders</Text>
						</TouchableOpacity>
					)}
					<TouchableOpacity
						style={[
							formStyle.button,
							{
								backgroundColor: isLoading ? Colors.greyscale[6] : global.orgColor,
								marginTop: 10,
								zIndex: -1,
							},
						]}
						onPress={() => {
							handleSubmit()
						}}
						disabled={isLoading}
					>
						{isLoading ? (
							<Image
								style={styles.loadingImage}
								source={require('../../assets/images/loadImg.gif')}
							/>
						) : (
							<Text style={formStyle.btnText}>
								{saveAddress ? 'SAVE & USE ADDRESS' : 'USE ADDRESS'}
							</Text>
						)}
					</TouchableOpacity>
				</View>
			)}
		</Formik>
	)
}

const styles = StyleSheet.create({
	formContainer: {
		width: '100%',
		alignSelf: 'center',
	},
	input: {
		marginTop: 8,
		marginBottom: 2,
		width: '100%',
	},
	loadingImage: {
		width: 90,
		height: 90,
		marginTop: -34,
		marginBottom: -34,
	},
	saveAddressToggle: {
		flexDirection: 'row',
		alignItems: 'center',
		marginVertical: 10,
	},
	saveAddressText: {
		marginLeft: 5,
		fontSize: smallTextSize,
		fontWeight: '700',
	},
})
